import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  TextField
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useSourceDialog } from '../SourceCreate.hooks';
import {
  connectionInputSchemaAtom,
  selectedCategoryAtom,
  selectedSourceDetailsAtom,
  selectedSubTimeIntervalAtom,
  selectedTimeIntervalAtom,
  subTimeIntervalOptionsAtom
} from '../SourceCreate.store';
import useStyles from '../SourceCreate.styles';
import { type DynamicFieldData, SourceDialogMode } from '../SourceCreate.types';

export type SubTimeIntervalProps = {
  subTimeIntervalSchema: DynamicFieldData[];
};

// For the JuniperMist Source Type 'Subtime Interval' dropdown, the options will load based on the selected TimeInterval.
// The options will be different for each TimeInterval.

const SubTimeInterval = ({ subTimeIntervalSchema }: SubTimeIntervalProps) => {
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ themeMode });

  // Form State
  const { control, formState, setValue } = useFormContext();
  const { errors } = formState;
  const [cat] = useAtom(selectedCategoryAtom);
  const [selectedTimeInterval] = useAtom(selectedTimeIntervalAtom);
  const [selectedSubTimeInterval, setSelectedSubTimeInterval] = useAtom(
    selectedSubTimeIntervalAtom
  );
  const [subTimeIntervalOptions, setSubTimeIntervalOptions] = useAtom(
    subTimeIntervalOptionsAtom
  );
  const { mode } = useSourceDialog();
  const [selectedSourceDetails] = useAtom(selectedSourceDetailsAtom);
  const [connectionInputSchema] = useAtom(connectionInputSchemaAtom);

  const handleSubTimeIntervalChange = (value: string) => {
    setSelectedSubTimeInterval(value);
  };

  useEffect(() => {
    const subTimeIntervalInputs =
      connectionInputSchema?.sub_time_interval_inputs?.find(
        (item) => item.time_interval === selectedTimeInterval
      );
    const options = subTimeIntervalInputs?.options ?? [];
    setSubTimeIntervalOptions(options);

    const defaultValue = subTimeIntervalInputs?.value
      ? subTimeIntervalInputs.value
      : '';
    setValue('sub_time_interval', defaultValue);
    setSelectedSubTimeInterval(
      Array.isArray(defaultValue) ? (defaultValue[0] ?? '') : defaultValue
    );

    if (
      mode === SourceDialogMode.Edit &&
      selectedTimeInterval === selectedSourceDetails?.sub_time_interval &&
      selectedSourceDetails?.sub_time_interval
    ) {
      setValue('sub_time_interval', selectedSourceDetails?.sub_time_interval);
      setSelectedSubTimeInterval(
        Array.isArray(selectedSourceDetails?.sub_time_interval)
          ? (selectedSourceDetails?.sub_time_interval[0] ?? '')
          : (selectedSourceDetails?.sub_time_interval ?? '')
      );
    }
  }, [selectedTimeInterval]);

  const renderSubTimeInterval = () => {
    const catSelected =
      cat.includes('Tickets') || cat.includes('GuestAuthorization');

    const shouldRender =
      catSelected && subTimeIntervalOptions && selectedSubTimeInterval;
    if (!shouldRender) return null;

    const hasError = Boolean(errors?.sub_time_interval);
    const errorMessage = (errors?.sub_time_interval?.message || '') as string;

    const renderSubTimeIntervalOption = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: string
    ) => <li {...props}>{option}</li>;

    return (
      <Grid size={{ xs: 12, sm: 12, md: 8 }}>
        <InputLabel htmlFor="sub-time-interval">
          Polling Interval - Tickets & Guest Auth <span>*</span>
        </InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name="sub_time_interval"
            rules={{
              required: 'Select Polling Interval - Tickets & Guest Auth'
            }}
            render={({ field: { onChange } }) => (
              <Autocomplete
                className={classes.multiSelectForm}
                options={subTimeIntervalOptions}
                getOptionLabel={(option) => option || ''}
                defaultValue={selectedSubTimeInterval}
                value={selectedSubTimeInterval}
                onChange={(_event, newValue) => {
                  onChange(newValue || '');
                  handleSubTimeIntervalChange(newValue || '');
                }}
                renderOption={renderSubTimeIntervalOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasError}
                    helperText={errorMessage}
                  />
                )}
              />
            )}
          />
          {hasError && <ErrorMessage message={errorMessage} />}
        </FormControl>
      </Grid>
    );
  };

  return <>{subTimeIntervalSchema && renderSubTimeInterval()}</>;
};

export default SubTimeInterval;
